import * as _ from 'lodash';
import { RuleCategory } from '../../../services/RuleCategory';
import { isActiveStatus, RunStatus, ScenarioParameterType } from './api/types';

export {
    JurisdictionType,
    isSandboxBrand,
    isSandboxJurisdiction
} from '../../../shared/models/jurisdictions';

// The shapes of the API, its statuses and its error codes are defined in one place: api/types.js
export {
    RunStatus,
    ScenarioParameterType,
    SimulationErrorCode,
    isActiveStatus,
    isTerminalStatus
} from './api/types';

/* What the simulation API (/admin/simulations) answers with, and the rules the screens follow. */

/* A run is followed by reading it every few seconds until it is in a terminal state. */
export const RUN_POLL_INTERVAL_MS = 3000;
/* The results of a run are refreshed less often than the run itself while it is active. */
export const RESULTS_POLL_EVERY_N_TICKS = 3;
/* The list of runs is read less often, and only while a run in it is active. */
export const RUNS_LIST_POLL_INTERVAL_MS = 5000;
/*
  Consecutive failed reads before a screen gives up on following. The backend restarts on every deploy, which takes a while, so
  this has to outlast a restart: with the delays below, about a minute and a half.
*/
export const MAX_CONSECUTIVE_POLL_FAILURES = 6;
export const POLL_BACKOFF_MAX_MS = 30000;

/*
  How many runs of each sandbox are queued or running, by the code of the sandbox (a sandbox with none is not in it). The server refuses
  to reset or delete a sandbox while it has one (409 WORKSPACE_BUSY), so the screen that offers those says so before the user tries.
*/
export const countActiveRunsByWorkspace = runs =>
    _.countBy(
        _.filter(runs, run => isActiveStatus(run.status)),
        'workspace'
    );

/* How long to wait before reading again: the interval, and twice as long after each read in a row that failed, up to a limit. */
export const getPollDelay = (failures, interval) =>
    Math.min(
        interval * Math.pow(2, failures),
        Math.max(POLL_BACKOFF_MAX_MS, interval)
    );

/* Whether what is shown is up to date: it is not while a read is being tried again, or after the screen gave up on reading. */
export const Connection = Object.freeze({
    OK: 'OK',
    RECONNECTING: 'RECONNECTING',
    LOST: 'LOST'
});

/* The name of a scenario in the catalog; the id for one that is not in it any more, or when the catalog is not there. */
export const getScenarioName = (scenariosById, id) =>
    _.get(scenariosById, [id, 'name'], id);

export const RunStatusLabel = Object.freeze({
    [RunStatus.QUEUED]: 'Queued',
    [RunStatus.RUNNING]: 'Running',
    [RunStatus.COMPLETED]: 'Completed',
    [RunStatus.FAILED]: 'Failed',
    [RunStatus.CANCELLED]: 'Cancelled',
    [RunStatus.INTERRUPTED]: 'Interrupted'
});

export const getRunStatusLabel = status => RunStatusLabel[status] || status;

export const RiskCategory = Object.freeze({
    AML: 'AML',
    RG: 'RG',
    FRAUD: 'FRAUD'
});

export const RiskCategoryLabel = Object.freeze({
    [RiskCategory.AML]: 'Anti Money Laundering',
    [RiskCategory.RG]: 'Responsible Gambling',
    [RiskCategory.FRAUD]: 'Fraud'
});

/* The risk configuration is read per RuleCategory, which names the same three things differently. */
export const riskCategoryToRuleCategory = category => {
    switch (category) {
        case RiskCategory.AML:
            return RuleCategory.ANTI_MONEY_LAUNDERING;
        case RiskCategory.RG:
            return RuleCategory.RESPONSIBLE_GAMBLING;
        case RiskCategory.FRAUD:
            return RuleCategory.FRAUD;
        default:
            return undefined;
    }
};

/*
  The catalog may grow a type of parameter that this build does not know. Such a parameter is not shown and not sent, so that the
  scenario is played with the server's default for it, and the form still works.
*/
export const isKnownParameterType = parameter =>
    !!parameter && _.includes(ScenarioParameterType, parameter.type);

/* A seed is a non negative long. It is kept as a string: the API writes longs as strings, and a JS number cannot hold them all. */
const MAX_SEED_DIGITS = 18;
export const isValidSeed = seed =>
    _.isNil(seed) ||
    seed === '' ||
    (/^\d+$/.test(String(seed)) && String(seed).length <= MAX_SEED_DIGITS);

/* How far a run has got: events that were processed or failed out of the events planned (0-100). */
export const getRunProgressPercent = progress => {
    const planned = _.get(progress, 'eventsPlanned', 0);
    if (!planned) {
        return 0;
    }
    const done =
        _.get(progress, 'eventsProcessed', 0) +
        _.get(progress, 'eventsFailed', 0);

    return Math.max(0, Math.min(100, Math.round((done / planned) * 100)));
};

/*
  Where a run stands in a "Test this change": the variant says which run is its baseline, and the baseline is a run with a rule set
  that changes nothing. Any other run is neither.
*/
export const RunRole = Object.freeze({
    BASELINE: 'BASELINE',
    VARIANT: 'VARIANT'
});

export const getRunRole = run => {
    if (!run) {
        return null;
    }
    if (run.baselineRunId) {
        return RunRole.VARIANT;
    }
    const changes = _.get(run, 'ruleSet.changes');
    return changes && _.isEmpty(changes.rules) && _.isEmpty(changes.riskRatios)
        ? RunRole.BASELINE
        : null;
};

/* ---- Scenario parameters ---- */

export const getParameterDefault = parameter => {
    if (!_.isNil(parameter.default)) {
        return parameter.default;
    }
    switch (parameter.type) {
        case ScenarioParameterType.BOOLEAN:
            return false;
        case ScenarioParameterType.STRING:
            return '';
        default:
            return parameter.min !== null && parameter.min !== undefined
                ? parameter.min
                : 0;
    }
};

export const getDefaultParameterValues = scenario =>
    _.reduce(
        _.filter((scenario && scenario.parameters) || [], isKnownParameterType),
        (values, parameter) => ({
            ...values,
            [parameter.key]: getParameterDefault(parameter)
        }),
        {}
    );

/* Returns the typed value of the text a user typed in for a parameter, or undefined when it is not valid for that type. */
export const parseParameterValue = (parameter, raw) => {
    switch (parameter.type) {
        case ScenarioParameterType.BOOLEAN:
            return raw === true || raw === 'true';
        case ScenarioParameterType.STRING:
            return _.isNil(raw) ? '' : String(raw);
        case ScenarioParameterType.INTEGER: {
            if (!/^-?\d+$/.test(String(raw).trim())) {
                return undefined;
            }
            return parseInt(String(raw).trim(), 10);
        }
        case ScenarioParameterType.DECIMAL: {
            const text = String(raw).trim();
            if (!text || isNaN(Number(text))) {
                return undefined;
            }
            return Number(text);
        }
        default:
            return raw;
    }
};

/* The message to show for a parameter value that is not acceptable, or null when it is. */
export const validateParameterValue = (parameter, raw) => {
    if (!isKnownParameterType(parameter)) {
        return null;
    }
    const value = parseParameterValue(parameter, raw);
    if (value === undefined) {
        return parameter.type === ScenarioParameterType.INTEGER
            ? 'Enter a whole number'
            : 'Enter a number';
    }
    const isNumber =
        parameter.type === ScenarioParameterType.INTEGER ||
        parameter.type === ScenarioParameterType.DECIMAL;
    if (isNumber) {
        if (!_.isNil(parameter.min) && value < parameter.min) {
            return `Must be at least ${parameter.min}`;
        }
        if (!_.isNil(parameter.max) && value > parameter.max) {
            return `Must be at most ${parameter.max}`;
        }
    }
    return null;
};

/*
  Builds the `scenarios` of a request for a run from what the form holds: [{ id, values }] where values are the typed values
  by parameter key. Only the values that differ from the default are sent, so the server's defaults stay the defaults.
*/
export const toScenarioRequests = (selected, scenariosById) =>
    _.map(selected, ({ id, values }) => {
        const scenario = scenariosById[id];
        const parameters = _.reduce(
            _.filter(
                (scenario && scenario.parameters) || [],
                isKnownParameterType
            ),
            (changed, parameter) => {
                const value = parseParameterValue(
                    parameter,
                    (values || {})[parameter.key]
                );
                return value !== undefined &&
                    value !== getParameterDefault(parameter)
                    ? { ...changed, [parameter.key]: value }
                    : changed;
            },
            {}
        );

        return _.isEmpty(parameters) ? { id } : { id, parameters };
    });

/* ---- A new run from an earlier one ("Run again") ---- */

/*
  What the form of a new run starts with when it is made from a run: the same sandbox, seed and label, and the same scenarios with the
  values of their parameters.

  The run carries `scenarioRequests`: the scenarios as they were asked for, in order, with the parameters that were given (none for a
  scenario played with its defaults). A backend that does not send them yet (before legaleyse-backend#90 is deployed) leaves the
  parameters at their defaults, and `parametersKept` says so, so that the form does not claim a repeat it cannot make.

  The form has each scenario once, so a scenario that the run played more than once (possible through the API, not through the form)
  is filled in with its first values only, and `repeated` names it: the same seed gives other players when a scenario is dropped.
  `unknownParameters` are the `scenario.parameter` keys the run was given that the catalog does not have any more.
  `missing` are the scenarios of the run that are not (or not any more) in the catalog, or that cannot be played now.
*/
export const planRunAgain = (run, workspaces, scenarios) => {
    const scenariosById = _.keyBy(scenarios, 'id');
    const playable = id =>
        scenariosById[id] && scenariosById[id].available !== false;
    const parametersKept = _.isArray(run && run.scenarioRequests);
    const requests = parametersKept
        ? run.scenarioRequests
        : _.map((run && run.scenarios) || [], id => ({ id, parameters: {} }));
    const ids = _.map(requests, 'id');
    const known = scenario =>
        _.map(_.filter(scenario.parameters, isKnownParameterType), 'key');

    const unknownParameters = [];
    const selected = _.reduce(
        _.filter(requests, request => playable(request.id)),
        (chosen, { id, parameters }) => {
            if (_.has(chosen, id)) {
                return chosen;
            }
            const scenario = scenariosById[id];
            const given = parameters || {};
            _.each(_.difference(_.keys(given), known(scenario)), key =>
                unknownParameters.push(`${id}.${key}`)
            );

            return {
                ...chosen,
                [id]: {
                    ...getDefaultParameterValues(scenario),
                    ..._.pick(given, known(scenario))
                }
            };
        },
        {}
    );

    return {
        workspace: _.some(workspaces, { code: run.workspace })
            ? run.workspace
            : null,
        workspaceMissing: !_.some(workspaces, { code: run.workspace }),
        seed: _.isNil(run.seed) ? '' : String(run.seed),
        label: run.label || '',
        selected,
        parametersKept,
        repeated: _.keys(_.pickBy(_.countBy(ids), count => count > 1)),
        unknownParameters,
        missing: _.uniq(_.reject(ids, playable))
    };
};

/* ---- Rule variables (of the rules of a workspace) ---- */

/*
  The rules API gives a variable as its value with the unit around it: "5[ times]" (a suffix), "[EUR]100" (a prefix) or just
  "5". The unit is for display; the configuration is set with the bare value.
*/
export const parseRuleVariable = text => {
    const value = _.isNil(text) ? '' : String(text);
    const prefixed = /^\[([^\]]*)\](.*)$/.exec(value);
    if (prefixed) {
        return {
            unit: prefixed[1].trim(),
            position: 'prefix',
            value: prefixed[2]
        };
    }
    const suffixed = /^(.*)\[([^\]]*)\]$/.exec(value);
    if (suffixed) {
        return {
            unit: suffixed[2].trim(),
            position: 'suffix',
            value: suffixed[1]
        };
    }
    return { unit: '', position: 'suffix', value };
};

// Moved to a shared helper, which the audit screen uses too; the simulation screens go on reading it from here.
export { humanize } from '../../../shared/helpers/text.helper';

/*
  What to select at the top to see the data of a run: the data of a run is the data of its brand, so its brand and the jurisdiction of
  that brand, from the list the selector is made from. Null when the run's brand is not in the list.
*/
export const findRunSelection = (run, brandsAndJurisdictions) => {
    const brand = _.find(
        _.get(brandsAndJurisdictions, 'brands', []),
        candidate => String(candidate.id) === String(_.get(run, 'brandId'))
    );
    const jurisdiction =
        brand &&
        _.find(
            _.get(brandsAndJurisdictions, 'jurisdictions', []),
            candidate => candidate.code === brand.jurisdiction
        );

    return jurisdiction ? { jurisdiction, brand } : null;
};

/* ---- Comparisons ---- */

export const MAX_COMPARISON_PLAYER_LIMIT = 1000;
export const DEFAULT_COMPARISON_PLAYER_LIMIT = 100;

/* What a "Test this change" request asks to change, from the form: rule edits and risk ratio edits. Empty when nothing changed. */
export const toRuleChangeRequests = edits =>
    _.filter(
        _.map(edits, edit => {
            const change = { ruleType: edit.ruleType };
            if (!_.isEmpty(edit.config)) {
                change.config = edit.config;
            }
            if (_.isBoolean(edit.enabled)) {
                change.enabled = edit.enabled;
            }
            return change;
        }),
        change => change.config || _.has(change, 'enabled')
    );

export const toRiskRatiosRequest = ratiosByCategory =>
    _.reduce(
        ratiosByCategory,
        (request, ratios, category) =>
            _.isEmpty(ratios) ? request : { ...request, [category]: ratios },
        {}
    );

/* ---- The changes of a "Test this change", worked out from what the form holds ---- */

/*
  rules: the rules of the workspace as the rules API gives them (ruleType, enabled, ruleVariables)
  edits: what the form holds by rule type: { config: { variable: bare value typed in }, enabled }

  A rule is changed by the variables whose value is not the one it has, and by its enabled flag if that is not what it is. A variable
  that is left empty is not a change: the rule keeps its value.
*/
export const diffRuleChanges = (rules, edits) =>
    toRuleChangeRequests(
        _.map(rules, rule => {
            const edit = (edits || {})[rule.ruleType];
            if (!edit) {
                return { ruleType: rule.ruleType };
            }
            const original = _.mapValues(
                rule.ruleVariables,
                text => parseRuleVariable(text).value
            );
            const config = _.pickBy(
                edit.config,
                (value, key) =>
                    _.trim(value) !== '' &&
                    _.trim(value) !== _.trim(original[key])
            );

            return {
                ruleType: rule.ruleType,
                config: _.mapValues(config, _.trim),
                enabled:
                    _.isBoolean(edit.enabled) && edit.enabled !== rule.enabled
                        ? edit.enabled
                        : undefined
            };
        })
    );

/* A ratio of a risk factor is a whole number from 0 to 100. */
export const isValidRatio = raw =>
    /^\d+$/.test(_.trim(raw)) && Number(_.trim(raw)) <= 100;

/*
  risk: the risk factors of the workspace by category, as the risk configuration API gives them (riskFactorKey, riskFactorWeight)
  edits: the ratios typed in, by category and then by the key of the factor

  Only the factors whose ratio is not the one they have are sent: the others keep theirs.
*/
export const diffRiskRatios = (risk, edits) =>
    toRiskRatiosRequest(
        _.mapValues(risk, (factors, category) =>
            _.reduce(
                factors,
                (changed, factor) => {
                    const raw = _.get(edits, [category, factor.riskFactorKey]);

                    return !_.isNil(raw) &&
                        isValidRatio(raw) &&
                        Number(_.trim(raw)) !== factor.riskFactorWeight
                        ? {
                              ...changed,
                              [factor.riskFactorKey]: Number(_.trim(raw))
                          }
                        : changed;
                },
                {}
            )
        )
    );

/* Whether a ratio that is typed in is one that cannot be sent, for the factors that have one typed in. */
export const hasInvalidRatio = edits =>
    _.some(edits, ratios => _.some(ratios, raw => !isValidRatio(raw)));

/*
  The description of a rule is a template: "User crossed EUR [${totalDeposits}] in deposits". Fills in the variables that it names
  with the values given (by the name of the variable), and leaves the ones it has no value for as they are.
*/
export const fillRuleDescription = (description, values) =>
    _.replace(
        String(description || ''),
        /\[?\$\{([^}]+)\}\]?/g,
        (match, name) => (_.has(values, name) ? values[name] : match)
    );
